import { MaterialKindEnum } from 'core/graphql';

import type {
  IGroupAlertTarget,
  RosterFilter,
  TabId,
} from 'core/utils/groupAlerts';
import type {
  IRosterSort,
  RosterSortKey,
  SortDir,
} from 'core/utils/groupRoster';

/**
 * Pure encode/parse logic for the GroupDetail deep-link state — the slice of
 * view state the dashboard persists in the URL so a reload (or a shared link)
 * restores the same tab, roster filter/search/sort and materials kind.
 *
 * The module is deliberately decoupled from React/Apollo/`react-router` so the
 * round-trip and totality guarantees can be property-tested in isolation
 * (design Properties 4, 7, 8). The `useGroupDeepLinkState` hook (task 9.5) is
 * the thin React wrapper that reads `location.search`, calls {@link parse},
 * and writes the result of {@link encode} back through history.
 *
 * Conventions:
 *   - `overview` is the default tab and is **omitted** from the URL — an absent
 *     `tab` parameter is equivalent to `overview`.
 *   - Every other field omits its default/empty value from the encoded params
 *     too, so the encoded query string stays minimal and the round-trip holds.
 *   - Parsing is **total**: each field is parsed independently and falls back
 *     to its own safe default on an unrecognized value, so {@link parse} never
 *     throws for arbitrary input.
 *
 * Shared types are imported rather than redeclared: `TabId`/`RosterFilter`/
 * `IGroupAlertTarget` come from `core/utils/groupAlerts` (their canonical
 * pure-layer home), and `IRosterSort`/`RosterSortKey`/`SortDir` come from
 * `core/utils/groupRoster`. `MaterialKindEnum` is the generated GraphQL enum
 * from `core/graphql`; it is a plain value enum, so it doubles as the runtime
 * source of recognized `kind` values without coupling to query/hook code.
 */

/** URL-encoded view state that survives a reload (design: `IGroupDeepLinkState`). */
export interface IGroupDeepLinkState {
  /** Active tab. Default `overview`, represented by an absent `tab` param. */
  tab: TabId;
  /** Students-tab roster filter chip. Default `all`. */
  rosterFilter: RosterFilter;
  /** Students-tab roster search query. Default `''`. */
  rosterQuery: string;
  /** Students-tab roster sort (`key:dir`). Default `null`. */
  rosterSort: IRosterSort | null;
  /** Materials-tab kind filter. Default `''` (no kind filter). */
  materialsKind: '' | MaterialKindEnum;
  /**
   * Communication-tab conversation preselect (the dashboard "waiting for a
   * reply" panel deep-links a group row as `?tab=communication&conv=<id>`).
   * Default `''` (no preselect); any string is accepted — it is an opaque id
   * validated by the backend, not an enumerated view value.
   */
  conv: string;
}

/** The URL parameter names each field is encoded under. */
const PARAM = {
  tab: 'tab',
  filter: 'filter',
  query: 'q',
  sort: 'sort',
  kind: 'kind',
  conv: 'conv',
} as const;

/** Per-field safe defaults — the value used when a param is absent/unrecognized. */
const DEFAULT_TAB: TabId = 'overview';
const DEFAULT_FILTER: RosterFilter = 'all';
const DEFAULT_QUERY = '';
const DEFAULT_SORT: IRosterSort | null = null;
const DEFAULT_KIND = '' as const;
const DEFAULT_CONV = '';

/** Recognized values for each enumerated field (mirrors the source unions). */
const TAB_IDS: readonly TabId[] = [
  'overview',
  'students',
  'schedule',
  'zoom',
  'pandadoc',
  'materials',
  'surveys',
  'homework',
  'communication',
  'mailings',
  'tasks',
  'finances',
  'diplomas',
];
const ROSTER_FILTERS: readonly RosterFilter[] = [
  'all',
  'active',
  'terminated',
  'debtors',
  'blocked',
];
const ROSTER_SORT_KEYS: readonly RosterSortKey[] = [
  'debt',
  'percentPaid',
  'name',
  'status',
];
const SORT_DIRS: readonly SortDir[] = ['asc', 'desc'];
const MATERIAL_KINDS: readonly MaterialKindEnum[] = Object.values(
  MaterialKindEnum,
);

const isTabId = (value: string | null): value is TabId =>
  value !== null && (TAB_IDS as readonly string[]).includes(value);

const isRosterFilter = (value: string | null): value is RosterFilter =>
  value !== null && (ROSTER_FILTERS as readonly string[]).includes(value);

const isRosterSortKey = (value: string): value is RosterSortKey =>
  (ROSTER_SORT_KEYS as readonly string[]).includes(value);

const isSortDir = (value: string): value is SortDir =>
  (SORT_DIRS as readonly string[]).includes(value);

const isMaterialKind = (value: string | null): value is MaterialKindEnum =>
  value !== null && (MATERIAL_KINDS as readonly string[]).includes(value);

/**
 * Parse a `key:dir` sort token into an {@link IRosterSort}, or `null` when the
 * token is absent or either half is unrecognized. Splitting on the first `:`
 * is safe because no sort key contains a colon.
 */
const parseSort = (raw: string | null): IRosterSort | null => {
  if (!raw) return DEFAULT_SORT;
  const separator = raw.indexOf(':');
  if (separator === -1) return DEFAULT_SORT;
  const key = raw.slice(0, separator);
  const dir = raw.slice(separator + 1);
  if (isRosterSortKey(key) && isSortDir(dir)) return { key, dir };
  return DEFAULT_SORT;
};

/**
 * Normalize the `search` input to a fresh {@link URLSearchParams}. Both branches
 * copy rather than alias, so neither {@link parse} nor its caller can mutate the
 * input. A leading `?` (as in `location.search`) is handled by the constructor.
 */
const toParams = (search: string | URLSearchParams): URLSearchParams =>
  new URLSearchParams(search);

/**
 * Encode a deep-link state into {@link URLSearchParams}, omitting any field that
 * holds its default/empty value (including the `overview` tab convention) so the
 * query string stays minimal. `parse(encode(state))` restores an equal state.
 */
export const encode = (state: IGroupDeepLinkState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.tab !== DEFAULT_TAB) params.set(PARAM.tab, state.tab);
  if (state.rosterFilter !== DEFAULT_FILTER) {
    params.set(PARAM.filter, state.rosterFilter);
  }
  if (state.rosterQuery !== DEFAULT_QUERY) {
    params.set(PARAM.query, state.rosterQuery);
  }
  if (state.rosterSort) {
    params.set(PARAM.sort, `${state.rosterSort.key}:${state.rosterSort.dir}`);
  }
  if (state.materialsKind !== DEFAULT_KIND) {
    params.set(PARAM.kind, state.materialsKind);
  }
  if (state.conv !== DEFAULT_CONV) {
    params.set(PARAM.conv, state.conv);
  }

  return params;
};

/**
 * Parse a query string (or {@link URLSearchParams}) into a deep-link state.
 *
 * Total and defensive: every field is parsed independently and falls back to
 * its own default on an unrecognized value, so this never throws regardless of
 * input. An absent `tab` resolves to `overview` per the URL convention.
 */
export const parse = (
  search: string | URLSearchParams,
): IGroupDeepLinkState => {
  const params = toParams(search);

  const rawTab = params.get(PARAM.tab);
  const tab: TabId = isTabId(rawTab) ? rawTab : DEFAULT_TAB;

  const rawFilter = params.get(PARAM.filter);
  const rosterFilter: RosterFilter = isRosterFilter(rawFilter)
    ? rawFilter
    : DEFAULT_FILTER;

  const rawQuery = params.get(PARAM.query);
  const rosterQuery = rawQuery ?? DEFAULT_QUERY;

  const rosterSort = parseSort(params.get(PARAM.sort));

  const rawKind = params.get(PARAM.kind);
  const materialsKind: '' | MaterialKindEnum = isMaterialKind(rawKind)
    ? rawKind
    : DEFAULT_KIND;

  const conv = params.get(PARAM.conv) ?? DEFAULT_CONV;

  return { tab, rosterFilter, rosterQuery, rosterSort, materialsKind, conv };
};

/**
 * Apply a single alert's navigation target to the current deep-link state.
 *
 * Replaces — never merges — the tab and roster filter from one alert target:
 * the target's `tab` becomes active, and its `filter` becomes the roster filter
 * (clearing any previously applied roster filter when the target carries none,
 * by falling back to `all`). This guarantees at most one alert's filter is ever
 * active, so conflicting cross-tab filters cannot accumulate. The remaining
 * view state (search query, sort, materials kind) is preserved untouched, and
 * `prevState` is never mutated.
 */
export const applyAlertNavigation = (
  prevState: IGroupDeepLinkState,
  target: IGroupAlertTarget,
): IGroupDeepLinkState => ({
  ...prevState,
  tab: target.tab,
  rosterFilter: target.filter ?? DEFAULT_FILTER,
});
